import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useI18n, MEDIA, BRAND } from "@/i18n";
import { Check, ArrowRight, Clock, Users, Tag } from "lucide-react";
import Nav from "@/components/Nav";
import FloatingActions from "@/components/FloatingActions";

// Per-slug hero image + accent config
const SLUG_CONFIG = {
  coppie: {
    hero: MEDIA.terrace,
    accent: "warm",
    price: 170,
    ldName: "Romantic Couples Cooking Class",
  },
  gruppo: {
    hero: MEDIA.gallery && MEDIA.gallery[3] ? MEDIA.gallery[3].src : MEDIA.hero,
    accent: "warm",
    price: 150,
    ldName: "Small Group Cooking Class",
  },
};

const VALID_SLUGS = Object.keys(SLUG_CONFIG);

export default function ExperienceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();

  // Guard against unknown slugs
  useEffect(() => {
    if (!VALID_SLUGS.includes(slug)) {
      navigate("/", { replace: true });
    }
  }, [slug, navigate]);

  const cfg = SLUG_CONFIG[slug];
  const base = cfg && t.classes ? t.classes[slug] : null;
  const details = cfg && t.experiences ? t.experiences[slug] : null;
  const ex = t.experiences;

  // Manage SEO tags dynamically
  useEffect(() => {
    if (!cfg || !base || !details || !ex) return;

    const originalTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const originalDesc = meta ? meta.getAttribute("content") : null;
    const canonical = document.querySelector('link[rel="canonical"]');
    const originalCanonical = canonical ? canonical.getAttribute("href") : null;

    document.title = details.metaTitle + ex.pageTitleSuffix;
    if (meta) meta.setAttribute("content", details.metaDescription);
    if (canonical) canonical.setAttribute("href", `https://eternalcityview.com/experiences/${slug}`);

    // JSON-LD structured data
    const ldScript = document.createElement("script");
    ldScript.type = "application/ld+json";
    ldScript.id = "experience-jsonld";
    ldScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      name: cfg.ldName,
      description: details.metaDescription,
      image: cfg.hero,
      brand: { "@type": "Brand", name: BRAND.name },
      offers: {
        "@type": "Offer",
        price: cfg.price,
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock",
        url: `https://eternalcityview.com/experiences/${slug}`,
      },
    });
    document.head.appendChild(ldScript);

    return () => {
      document.title = originalTitle;
      if (meta && originalDesc !== null) meta.setAttribute("content", originalDesc);
      if (canonical && originalCanonical !== null) canonical.setAttribute("href", originalCanonical);
      const existing = document.getElementById("experience-jsonld");
      if (existing) existing.remove();
    };
  }, [slug, lang, cfg, base, details, ex]);

  // Scroll to top on mount / slug change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!cfg || !base || !details || !ex) return null;

  const isDark = cfg.accent === "dark";
  const otherSlugs = VALID_SLUGS.filter((s) => s !== slug);

  return (
    <div className="bg-[#FAF9F6] min-h-screen">
      <Nav />

      {/* Hero */}
      <section
        className="relative min-h-[70vh] sm:min-h-[80vh] flex items-end overflow-hidden"
        data-testid={`experience-hero-${slug}`}
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${cfg.hero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#14110D]/95 via-[#14110D]/55 to-[#14110D]/20" />
        <div className="absolute inset-0 grain" />

        <div className="relative max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-10 pb-16 sm:pb-24 pt-32 text-[#FAF9F6]">
          <Link
            to="/#classes"
            className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-[#FAF9F6]/70 hover:text-[#C85A3C] transition"
            data-testid="experience-back-classes"
          >
            {ex.backToAll}
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="overline text-[#D4B98D]">{base.persons}</span>
            {base.badge && (
              <span className="text-[10px] tracking-widest uppercase px-2.5 py-1 bg-[#C85A3C] text-[#FAF9F6] rounded-sm">
                {ex.badgePremium}
              </span>
            )}
          </div>

          <h1
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-3 max-w-4xl"
            data-testid={`experience-title-${slug}`}
          >
            {base.title}
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-[#FAF9F6]/85 max-w-2xl leading-relaxed">
            {details.heroTagline}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#D4B98D]" strokeWidth={1.6} />
              <span className="text-[#FAF9F6]/85">
                <span className="text-[#FAF9F6]/50 mr-1">{ex.durationLabel}:</span>
                {details.duration}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[#D4B98D]" strokeWidth={1.6} />
              <span className="text-[#FAF9F6]/85">
                <span className="text-[#FAF9F6]/50 mr-1">{ex.guestsLabel}:</span>
                {base.persons}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-[#D4B98D]" strokeWidth={1.6} />
              <span className="text-[#FAF9F6]/85">
                <span className="text-[#FAF9F6]/50 mr-1">{ex.priceLabel}:</span>
                <span className="font-serif-display text-lg text-[#FAF9F6]">{base.price}</span>
              </span>
            </div>
          </div>

          <Link
            to={`/?class=${slug}#booking`}
            data-testid={`experience-book-hero-${slug}`}
            className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 bg-[#C85A3C] text-white text-sm tracking-widest uppercase hover:bg-[#B24A2E] transition rounded-sm"
          >
            {ex.bookNow} <ArrowRight className="w-4 h-4" strokeWidth={1.6} />
          </Link>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <p className="overline">{t.classes.overline}</p>
            <p className="mt-4 text-lg leading-relaxed text-[#2C2520]">{details.descLong}</p>

            {/* Includes */}
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-16 mb-6 text-[#1F1A14]">
              {ex.includesTitle}
            </h2>
            <ul className="space-y-3" data-testid={`experience-includes-${slug}`}>
              {details.includes.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#C85A3C] mt-0.5 shrink-0" strokeWidth={1.8} />
                  <span className="text-base text-[#2C2520]/85 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>

            {/* Flow */}
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-16 mb-8 text-[#1F1A14]">
              {ex.flowTitle}
            </h2>
            <ol className="space-y-6" data-testid={`experience-flow-${slug}`}>
              {details.flow.map((step, i) => (
                <li key={i} className="grid grid-cols-[auto_1fr] gap-4 items-start">
                  <span className="font-serif-display text-2xl text-[#B89668]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-base text-[#2C2520]/85 leading-relaxed pt-1">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Sticky sidebar with CTA + Why */}
          <aside className="lg:col-span-5">
            <div className={`sticky top-24 p-8 rounded-sm ${isDark ? "bg-[#1F1A14] text-[#FAF9F6]" : "bg-[#F2EFEB] text-[#1F1A14] border border-[#E5E0D8]"}`}>
              <p className={`overline ${isDark ? "text-[#D4B98D]" : ""}`}>{base.persons}</p>
              <h3 className={`font-serif-display text-3xl mt-2 ${isDark ? "text-[#FAF9F6]" : "text-[#1F1A14]"}`}>
                {base.title}
              </h3>
              <div className={`mt-6 flex items-baseline gap-2 ${isDark ? "text-[#D4B98D]" : "text-[#B89668]"}`}>
                <span className="font-serif-display text-4xl">{base.price}</span>
              </div>

              <div className="mt-6 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className={isDark ? "text-[#FAF9F6]/60" : "text-[#5C564F]"}>{ex.durationLabel}</span>
                  <span>{details.duration}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? "text-[#FAF9F6]/60" : "text-[#5C564F]"}>{ex.guestsLabel}</span>
                  <span>{base.persons}</span>
                </div>
              </div>

              <Link
                to={`/?class=${slug}#booking`}
                data-testid={`experience-book-sidebar-${slug}`}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#C85A3C] text-white text-sm tracking-widest uppercase hover:bg-[#B24A2E] transition rounded-sm"
              >
                {ex.bookNow} <ArrowRight className="w-4 h-4" strokeWidth={1.6} />
              </Link>

              <div className={`mt-8 pt-6 border-t ${isDark ? "border-[#FAF9F6]/15" : "border-[#2C2520]/10"}`}>
                <p className={`overline mb-4 ${isDark ? "text-[#D4B98D]" : ""}`}>{ex.whyTitle}</p>
                <ul className="space-y-2">
                  {details.why.map((w, i) => (
                    <li key={i} className={`flex items-start gap-2 text-sm ${isDark ? "text-[#FAF9F6]/80" : "text-[#2C2520]/75"}`}>
                      <span className="text-[#C85A3C] mt-1">•</span>
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Other experiences */}
      <section className="bg-[#F2EFEB] py-20 sm:py-24 border-t border-[#E5E0D8]" data-testid="experience-related">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
          <p className="overline">{ex.otherTitle}</p>
          <div className="mt-8 grid md:grid-cols-3 gap-4 md:gap-6">
            {otherSlugs.slice(0, 3).map((s) => {
              const otherBase = t.classes[s];
              const otherCfg = SLUG_CONFIG[s];
              return (
                <Link
                  key={s}
                  to={`/experiences/${s}`}
                  data-testid={`experience-related-${s}`}
                  className="group block relative overflow-hidden rounded-sm bg-[#1F1A14] aspect-[4/5] hover-lift"
                >
                  <img
                    src={otherCfg.hero}
                    alt={otherBase.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#14110D]/95 via-[#14110D]/30 to-transparent" />
                  <div className="relative h-full flex flex-col justify-end p-6 text-[#FAF9F6]">
                    <p className="overline text-[#D4B98D]">{otherBase.persons}</p>
                    <p className="font-serif-display text-2xl mt-2 leading-tight">{otherBase.title}</p>
                    <p className="mt-2 text-sm text-[#FAF9F6]/70">{otherBase.price}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-xs tracking-widest uppercase text-[#FAF9F6] group-hover:text-[#C85A3C] transition">
                      {t.classes.detailsCta || "Learn more"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/"
              className="text-sm text-[#2C2520]/60 hover:text-[#C85A3C] transition"
              data-testid="experience-home-link"
            >
              {ex.backHome}
            </Link>
          </div>
        </div>
      </section>

      <FloatingActions />
    </div>
  );
}
